import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus, MoreVertical, FileSearch, Upload, Download, Loader2, FileText,
  CheckCircle, AlertTriangle, Trash2, Eye, X, Mail, FilePen, PenLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { parcelVerificationsAPI, clientsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { SignaturePad } from '../components/SignaturePad';

const DOC_TYPES = [
  { value: 'titre_foncier', label: 'Titre Foncier' },
  { value: 'lettre_attribution', label: "Lettre d'attribution" },
  { value: 'concession_rurale', label: 'Concession rurale' },
  { value: 'concession_urbaine', label: 'Concession urbaine' },
  { value: 'permis_habitation', label: "Permis d'habitation" },
  { value: 'permis_occuper', label: "Permis d'occuper" },
  { value: 'lettre_transfert', label: 'Lettre de transfert' },
  { value: 'bulletin', label: 'Bulletin' },
  { value: 'acte_vente', label: 'Acte de vente' },
  { value: 'autre', label: 'Autre' },
];

const STATUS_BADGES = {
  en_attente: { label: 'En attente', cls: 'badge-status badge-late' },
  en_cours: { label: 'En cours', cls: 'badge-status badge-partial' },
  conforme: { label: 'Conforme', cls: 'badge-status badge-validated' },
  non_conforme: { label: 'Non conforme', cls: 'badge-status badge-unpaid' },
  annule: { label: 'Annule', cls: 'badge-status badge-draft' },
};

const ParcelVerifications = () => {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureParty, setSignatureParty] = useState(null); // 'transferor' | 'transferee'
  const [savingSignature, setSavingSignature] = useState(false);
  const fileInputRef = useRef(null);

  const initialForm = {
    request_type: 'verification',
    client_id: '',
    requester_name: '',
    requester_phone: '',
    requester_email: '',
    requester_address: '',
    requester_id_number: '',
    location: '',
    parcel_reference: '',
    district: '',
    owner_claimed_name: '',
    document_type: 'titre_foncier',
    document_number: '',
    description: '',
    fee_amount: '',
    notes: '',
    recipient_authority: '',
    recipient_address: '',
    recipient_city: '',
    transferor_name: '',
    transferor_phone: '',
    transferor_id_number: '',
    transferee_name: '',
    transferee_phone: '',
    transferee_id_number: '',
    transfer_reason: '',
    parcel_area_m2: '',
    parcel_boundaries: '',
  };
  const [form, setForm] = useState(initialForm);
  const [reqMode, setReqMode] = useState('external'); // 'client' | 'external'

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, cRes] = await Promise.all([
        parcelVerificationsAPI.getAll(),
        clientsAPI.getAll(),
      ]);
      setItems(vRes.data || []);
      setClients(cRes.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setReqMode('external');
    setShowModal(true);
  };

  const openEdit = (rec) => {
    setEditing(rec);
    setForm({
      request_type: rec.request_type || 'verification',
      client_id: rec.client_id || '',
      requester_name: rec.requester_name || '',
      requester_phone: rec.requester_phone || '',
      requester_email: rec.requester_email || '',
      requester_address: rec.requester_address || '',
      requester_id_number: rec.requester_id_number || '',
      location: rec.location || '',
      parcel_reference: rec.parcel_reference || '',
      district: rec.district || '',
      owner_claimed_name: rec.owner_claimed_name || '',
      document_type: rec.document_type || 'titre_foncier',
      document_number: rec.document_number || '',
      description: rec.description || '',
      fee_amount: rec.fee_amount ? String(rec.fee_amount) : '',
      notes: rec.notes || '',
      recipient_authority: rec.recipient_authority || '',
      recipient_address: rec.recipient_address || '',
      recipient_city: rec.recipient_city || '',
      transferor_name: rec.transferor_name || '',
      transferor_phone: rec.transferor_phone || '',
      transferor_id_number: rec.transferor_id_number || '',
      transferee_name: rec.transferee_name || '',
      transferee_phone: rec.transferee_phone || '',
      transferee_id_number: rec.transferee_id_number || '',
      transfer_reason: rec.transfer_reason || '',
      parcel_area_m2: rec.parcel_area_m2 ? String(rec.parcel_area_m2) : '',
      parcel_boundaries: rec.parcel_boundaries || '',
    });
    setReqMode(rec.client_id ? 'client' : 'external');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.location) { toast.error('Localisation requise'); return; }
    if (reqMode === 'external' && !form.requester_name) { toast.error('Nom du demandeur requis'); return; }
    if (reqMode === 'client' && !form.client_id) { toast.error('Sélectionnez un client'); return; }
    if (form.request_type === 'transfert') {
      if (!form.transferor_name) { toast.error('Nom du cédant requis pour un transfert'); return; }
      if (!form.transferee_name) { toast.error('Nom du cessionnaire requis pour un transfert'); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        client_id: reqMode === 'client' ? form.client_id : null,
        fee_amount: parseFloat(form.fee_amount || 0) || 0,
        parcel_area_m2: form.parcel_area_m2 ? parseFloat(form.parcel_area_m2) : null,
      };
      if (editing) {
        await parcelVerificationsAPI.update(editing.id, payload);
        toast.success('Dossier mis à jour');
      } else {
        await parcelVerificationsAPI.create(payload);
        const labels = { verification: 'Demande de vérification créée', transfert: 'Demande de transfert créée', immatriculation: 'Demande d\'immatriculation créée' };
        toast.success(labels[form.request_type] || 'Dossier créé');
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (rec, status) => {
    try {
      await parcelVerificationsAPI.update(rec.id, { status });
      toast.success('Statut mis à jour');
      fetchData();
      if (selected?.id === rec.id) {
        const refreshed = await parcelVerificationsAPI.getById(rec.id);
        setSelected(refreshed.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (rec) => {
    if (!window.confirm('Supprimer cette vérification ? Les fichiers seront aussi supprimés.')) return;
    try {
      await parcelVerificationsAPI.delete(rec.id);
      toast.success('Supprimée');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const openDetail = (rec) => {
    setSelected(rec);
    setShowDetailModal(true);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    try {
      const res = await parcelVerificationsAPI.uploadDocument(selected.id, file);
      setSelected(res.data);
      toast.success('Document ajouté');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    }
    e.target.value = '';
  };

  const handleDeleteDoc = async (fileId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      const res = await parcelVerificationsAPI.deleteDocument(selected.id, fileId);
      setSelected(res.data);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleConclude = async (verdict) => {
    if (!selected) return;
    const findings = window.prompt('Conclusion / observations (optionnel) :', selected.findings || '');
    if (findings === null) return;
    try {
      const res = await parcelVerificationsAPI.update(selected.id, { status: verdict, findings });
      setSelected(res.data);
      toast.success('Verdict enregistré');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDownloadReport = async (rec) => {
    try {
      const r = await parcelVerificationsAPI.downloadReportPdf(rec.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_verification_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur de téléchargement');
    }
  };

  const handleDownloadRequest = async (rec) => {
    try {
      const r = await parcelVerificationsAPI.downloadRequestPdf(rec.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `demande_verification_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Lettre de demande téléchargée');
    } catch (e) {
      toast.error('Erreur de téléchargement');
    }
  };

  const handleDownloadCessionDeed = async (rec) => {
    try {
      const r = await parcelVerificationsAPI.downloadCessionDeedPdf(rec.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `acte_cession_${rec.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Acte de cession téléchargé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de téléchargement');
    }
  };

  const openSignatureModal = (party) => {
    setSignatureParty(party);
    setShowSignatureModal(true);
  };

  const handleSaveSignature = async (dataUrl) => {
    if (!selected || !signatureParty) return;
    setSavingSignature(true);
    try {
      const key = signatureParty === 'transferor' ? 'transferor_signature' : 'transferee_signature';
      const res = await parcelVerificationsAPI.saveCessionSignatures(selected.id, { [key]: dataUrl });
      const party_label = signatureParty === 'transferor' ? 'du cédant' : 'du cessionnaire';
      toast.success(`Signature ${party_label} enregistrée`);
      // Refresh local selected + list
      setSelected(prev => ({
        ...prev,
        transferor_signed_at: res.data.transferor_signed_at,
        transferee_signed_at: res.data.transferee_signed_at,
        transferor_signature: signatureParty === 'transferor' ? dataUrl.split(',')[1] : prev.transferor_signature,
        transferee_signature: signatureParty === 'transferee' ? dataUrl.split(',')[1] : prev.transferee_signature,
      }));
      setShowSignatureModal(false);
      setSignatureParty(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSavingSignature(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <FileSearch className="w-7 h-7 text-amber-600" />
            Dossiers de Parcelle
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Vérifications, transferts et immatriculations. Génération de lettre + rapport PDF officiel.
          </p>
        </div>
        <Button onClick={openCreate} className="btn-gold-premium" data-testid="new-parcel-verif-btn">
          <Plus className="w-4 h-4" /> Nouveau dossier
        </Button>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-base">Demandes ({items.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <FileSearch className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <p>Aucun dossier enregistré.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 px-2">Référence</th>
                    <th className="py-2 px-2">Type</th>
                    <th className="py-2 px-2">Demandeur</th>
                    <th className="py-2 px-2">Parcelle / Localisation</th>
                    <th className="py-2 px-2">Type document</th>
                    <th className="py-2 px-2">Statut</th>
                    <th className="py-2 px-2 text-right">Frais</th>
                    <th className="py-2 px-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((rec) => (
                    <tr key={rec.id} className="row-premium border-b border-gray-100 text-sm" data-testid={`pv-row-${rec.id}`}>
                      <td className="py-3 px-2 font-mono text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{rec.reference}</span>
                          {rec.request_type === 'transfert' && (rec.transferor_signed_at || rec.transferee_signed_at) && (() => {
                            const both = rec.transferor_signed_at && rec.transferee_signed_at;
                            const cls = both ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-amber-100 text-amber-700 border-amber-300';
                            const latest = [rec.transferor_signed_at, rec.transferee_signed_at].filter(Boolean).sort().slice(-1)[0];
                            const dateStr = latest ? new Date(latest).toLocaleDateString('fr-FR') : '';
                            return (
                              <span
                                title={both ? `Acte signé électroniquement le ${dateStr} (cédant + cessionnaire)` : "1/2 signature électronique enregistrée"}
                                className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${cls}`}
                                data-testid={`sig-badge-${rec.id}`}
                              >
                                <PenLine className="w-2.5 h-2.5" />
                                {both ? 'Signé' : '1/2'}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {(() => {
                          const t = rec.request_type || 'verification';
                          const cfg = {
                            verification: { label: 'Vérif.', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
                            transfert: { label: 'Transfert', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
                            immatriculation: { label: 'Immatric.', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                          }[t] || { label: t, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
                          return (
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-2">
                        <div className="font-semibold text-[#1B3A4B]">{rec.requester_name}</div>
                        {rec.requester_phone && <div className="text-xs text-gray-500">{rec.requester_phone}</div>}
                      </td>
                      <td className="py-3 px-2">
                        <div className="font-medium">{rec.location}</div>
                        {rec.parcel_reference && <div className="text-xs text-gray-500">{rec.parcel_reference}</div>}
                      </td>
                      <td className="py-3 px-2 text-xs">
                        {DOC_TYPES.find(d => d.value === rec.document_type)?.label || rec.document_type}
                      </td>
                      <td className="py-3 px-2">
                        <span className={STATUS_BADGES[rec.status]?.cls || 'badge-status badge-draft'}>
                          {STATUS_BADGES[rec.status]?.label || rec.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right tabular-nums">
                        {rec.fee_amount > 0 ? formatCurrency(rec.fee_amount, rec.currency || 'XOF') : '-'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`pv-actions-${rec.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(rec)}>
                              <Eye className="w-4 h-4 mr-2" /> Voir / Gérer
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(rec)}>
                              <FileText className="w-4 h-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadRequest(rec)} className="text-amber-700">
                              <Mail className="w-4 h-4 mr-2" /> Lettre de demande (à envoyer)
                            </DropdownMenuItem>
                            {rec.request_type === 'transfert' && (
                              <DropdownMenuItem onClick={() => handleDownloadCessionDeed(rec)} className="text-purple-700">
                                <FilePen className="w-4 h-4 mr-2" /> Acte de cession
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDownloadReport(rec)}>
                              <Download className="w-4 h-4 mr-2" /> Rapport PDF
                            </DropdownMenuItem>
                            {rec.status === 'en_attente' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(rec, 'en_cours')} className="text-blue-700">
                                Démarrer (En cours)
                              </DropdownMenuItem>
                            )}
                            {(rec.status === 'en_attente' || rec.status === 'en_cours') && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusChange(rec, 'conforme')} className="text-emerald-700">
                                  <CheckCircle className="w-4 h-4 mr-2" /> Marquer Conforme
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(rec, 'non_conforme')} className="text-red-600">
                                  <AlertTriangle className="w-4 h-4 mr-2" /> Marquer Non conforme
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => handleDelete(rec)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onOpenChange={(o) => setShowModal(o)}>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-amber-600" />
              {editing ? 'Modifier le dossier' : 'Nouveau dossier de parcelle'}
            </DialogTitle>
            <DialogDescription>Vérification de papiers, transfert de parcelle ou demande d&apos;immatriculation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Request type selector */}
            <div>
              <Label>Type de demande *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { v: 'verification', label: 'Vérification', desc: 'Authenticité des papiers' },
                  { v: 'transfert', label: 'Transfert', desc: 'Cédant → Cessionnaire' },
                  { v: 'immatriculation', label: 'Immatriculation', desc: 'Création de titre foncier' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, request_type: opt.v })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      form.request_type === opt.v
                        ? 'border-amber-500 bg-amber-50 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                    data-testid={`request-type-${opt.v}`}
                  >
                    <div className={`text-sm font-bold ${form.request_type === opt.v ? 'text-amber-700' : 'text-gray-700'}`}>
                      {opt.label}
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <Button type="button" variant={reqMode === 'client' ? 'default' : 'outline'} size="sm" onClick={() => setReqMode('client')}>
                Client existant
              </Button>
              <Button type="button" variant={reqMode === 'external' ? 'default' : 'outline'} size="sm" onClick={() => setReqMode('external')}>
                Demandeur externe
              </Button>
            </div>
            {reqMode === 'client' ? (
              <div>
                <Label>Client *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || `${c.first_name || ''} ${c.last_name || ''}`} {c.phone ? `· ${c.phone}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Nom du demandeur *</Label>
                  <Input value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input value={form.requester_phone} onChange={(e) => setForm({ ...form, requester_phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Localisation / Adresse *</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex: Bamako, Sebenikoro" />
              </div>
              <div>
                <Label>Quartier / Cercle</Label>
                <Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
              </div>
              <div>
                <Label>Référence parcelle</Label>
                <Input value={form.parcel_reference} onChange={(e) => setForm({ ...form, parcel_reference: e.target.value })} placeholder="Ex: Lot N°123 Section A" />
              </div>
              <div>
                <Label>Propriétaire déclaré</Label>
                <Input value={form.owner_claimed_name} onChange={(e) => setForm({ ...form, owner_claimed_name: e.target.value })} />
              </div>
              <div>
                <Label>Type de document *</Label>
                <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map(d => (
                      <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N° du document</Label>
                <Input value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
              </div>
              <div>
                <Label>Frais (XOF)</Label>
                <Input type="number" value={form.fee_amount} onChange={(e) => setForm({ ...form, fee_amount: e.target.value })} placeholder="0" />
              </div>
            </div>

            {/* === TRANSFERT specific fields === */}
            {form.request_type === 'transfert' && (
              <div className="rounded-xl border-2 border-amber-200 bg-amber-50/40 p-4 space-y-3" data-testid="transfer-section">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center text-xs font-bold">T</div>
                  <h4 className="font-bold text-sm text-amber-900">Parties au transfert</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Cédant (ancien propriétaire) *</Label>
                    <Input value={form.transferor_name} onChange={(e) => setForm({ ...form, transferor_name: e.target.value })} data-testid="transferor-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone cédant</Label>
                    <Input value={form.transferor_phone} onChange={(e) => setForm({ ...form, transferor_phone: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Pièce d&apos;identité cédant</Label>
                    <Input value={form.transferor_id_number} onChange={(e) => setForm({ ...form, transferor_id_number: e.target.value })} placeholder="NIN / CNI" />
                  </div>
                  <div>
                    <Label className="text-xs">Cessionnaire (nouveau propriétaire) *</Label>
                    <Input value={form.transferee_name} onChange={(e) => setForm({ ...form, transferee_name: e.target.value })} data-testid="transferee-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Téléphone cessionnaire</Label>
                    <Input value={form.transferee_phone} onChange={(e) => setForm({ ...form, transferee_phone: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Pièce d&apos;identité cessionnaire</Label>
                    <Input value={form.transferee_id_number} onChange={(e) => setForm({ ...form, transferee_id_number: e.target.value })} placeholder="NIN / CNI" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Motif du transfert</Label>
                  <Input value={form.transfer_reason} onChange={(e) => setForm({ ...form, transfer_reason: e.target.value })} placeholder="Ex: Vente notariée, donation, héritage..." />
                </div>
              </div>
            )}

            {/* === IMMATRICULATION specific fields === */}
            {form.request_type === 'immatriculation' && (
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="registration-section">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-500 text-white flex items-center justify-center text-xs font-bold">I</div>
                  <h4 className="font-bold text-sm text-blue-900">Caractéristiques de la parcelle</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Superficie (m²)</Label>
                    <Input type="number" step="0.01" value={form.parcel_area_m2} onChange={(e) => setForm({ ...form, parcel_area_m2: e.target.value })} placeholder="Ex: 425.50" />
                  </div>
                  <div>
                    <Label className="text-xs">Pièce d&apos;identité demandeur</Label>
                    <Input value={form.requester_id_number} onChange={(e) => setForm({ ...form, requester_id_number: e.target.value })} placeholder="NIN / CNI" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Limites / Voisins</Label>
                  <Textarea rows={2} value={form.parcel_boundaries} onChange={(e) => setForm({ ...form, parcel_boundaries: e.target.value })} placeholder="Nord: rue X, Sud: parcelle Y, Est: ..., Ouest: ..." />
                </div>
              </div>
            )}

            <div>
              <Label>Description / Contexte</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            {/* === Destinataire de la lettre === */}
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3" data-testid="recipient-section">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-500" />
                <h4 className="font-bold text-sm text-gray-700">Destinataire de la lettre</h4>
              </div>
              <div>
                <Label className="text-xs">Service / Autorité destinataire</Label>
                <Input
                  value={form.recipient_authority}
                  onChange={(e) => setForm({ ...form, recipient_authority: e.target.value })}
                  placeholder="Ex: Direction Nationale des Domaines et du Cadastre"
                  data-testid="recipient-authority"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Laissez vide pour utiliser l&apos;autorité suggérée par défaut selon le type de demande.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Adresse du destinataire</Label>
                  <Input value={form.recipient_address} onChange={(e) => setForm({ ...form, recipient_address: e.target.value })} placeholder="Ex: BP 233, Bd de la Liberté" />
                </div>
                <div>
                  <Label className="text-xs">Ville</Label>
                  <Input value={form.recipient_city} onChange={(e) => setForm({ ...form, recipient_city: e.target.value })} placeholder="Ex: Bamako" />
                </div>
              </div>
            </div>
            <div>
              <Label>Notes internes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={submitting}>Annuler</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="btn-gold-premium" data-testid="save-parcel-verif-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              {editing ? 'Mettre à jour' : 'Créer la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={(o) => setShowDetailModal(o)}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5 text-amber-600" />
              {selected?.reference}
            </DialogTitle>
            <DialogDescription>
              {selected?.requester_name} · {selected?.location}
            </DialogDescription>
          </DialogHeader>
          <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileSelected} />
          {selected && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={STATUS_BADGES[selected.status]?.cls}>
                  {STATUS_BADGES[selected.status]?.label}
                </span>
                <span className="text-sm text-gray-500">·</span>
                <span className="text-sm text-gray-700">
                  {DOC_TYPES.find(d => d.value === selected.document_type)?.label}
                  {selected.document_number ? ` · N° ${selected.document_number}` : ''}
                </span>
                {selected.fee_amount > 0 && (
                  <>
                    <span className="text-sm text-gray-500">·</span>
                    <span className="text-sm font-semibold">{formatCurrency(selected.fee_amount, selected.currency || 'XOF')}</span>
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => fileInputRef.current?.click()} className="bg-[#1B3A4B] hover:bg-[#264557]">
                  <Upload className="w-4 h-4 mr-1" /> Téléverser un document
                </Button>
                <Button size="sm" onClick={() => handleDownloadRequest(selected)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="pv-download-request-btn">
                  <Mail className="w-4 h-4 mr-1" /> Lettre de demande
                </Button>
                {selected.request_type === 'transfert' && (
                  <>
                    <Button size="sm" onClick={() => handleDownloadCessionDeed(selected)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="pv-download-cession-deed-btn">
                      <FilePen className="w-4 h-4 mr-1" /> Acte de cession
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openSignatureModal('transferor')} className={`border-2 ${selected.transferor_signed_at ? 'border-emerald-400 text-emerald-700 bg-emerald-50' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`} data-testid="pv-sign-transferor-btn">
                      <PenLine className="w-4 h-4 mr-1" /> {selected.transferor_signed_at ? 'Signé (cédant)' : 'Signer (cédant)'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openSignatureModal('transferee')} className={`border-2 ${selected.transferee_signed_at ? 'border-emerald-400 text-emerald-700 bg-emerald-50' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}`} data-testid="pv-sign-transferee-btn">
                      <PenLine className="w-4 h-4 mr-1" /> {selected.transferee_signed_at ? 'Signé (cess.)' : 'Signer (cessionnaire)'}
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => handleDownloadReport(selected)}>
                  <Download className="w-4 h-4 mr-1" /> Rapport PDF
                </Button>
                {(selected.status === 'en_attente' || selected.status === 'en_cours') && (
                  <>
                    <Button size="sm" onClick={() => handleConclude('conforme')} className="bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle className="w-4 h-4 mr-1" /> Conforme
                    </Button>
                    <Button size="sm" onClick={() => handleConclude('non_conforme')} className="bg-red-600 hover:bg-red-700">
                      <AlertTriangle className="w-4 h-4 mr-1" /> Non conforme
                    </Button>
                  </>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#1B3A4B] mb-2">Documents ({selected.documents?.length || 0})</h3>
                {(selected.documents || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Aucun document téléversé.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.documents.map((d) => (
                      <div key={d.file_id} className="flex items-center justify-between p-2 border border-gray-200 rounded">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-2"
                        >
                          <FileText className="w-4 h-4" /> {d.filename}
                        </a>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteDoc(d.file_id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selected.findings && (
                <div className="p-3 rounded border border-amber-200 bg-amber-50">
                  <div className="text-xs uppercase font-semibold text-amber-700 mb-1">Conclusion</div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selected.findings}</p>
                </div>
              )}
              {selected.notes && (
                <div className="p-3 rounded border border-gray-200 bg-gray-50">
                  <div className="text-xs uppercase font-semibold text-gray-600 mb-1">Notes internes</div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selected.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Electronic signature dialog for the cession deed */}
      <Dialog open={showSignatureModal} onOpenChange={(open) => {
        setShowSignatureModal(open);
        if (!open) setSignatureParty(null);
      }}>
        <DialogContent className="sm:max-w-[560px]" data-testid="pv-signature-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-purple-600" />
              Signature électronique — {signatureParty === 'transferor' ? 'Cédant' : 'Cessionnaire'}
            </DialogTitle>
            <DialogDescription>
              {signatureParty === 'transferor'
                ? <>Signature de <b>{selected?.transferor_name || '—'}</b>. En signant, vous déclarez <em>« Lu et approuvé, bon pour cession »</em>.</>
                : <>Signature de <b>{selected?.transferee_name || '—'}</b>. En signant, vous déclarez <em>« Lu et approuvé, bon pour acceptation »</em>.</>
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {savingSignature ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
              </div>
            ) : (
              <SignaturePad
                width={480}
                height={200}
                onSave={handleSaveSignature}
                onCancel={() => { setShowSignatureModal(false); setSignatureParty(null); }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParcelVerifications;
